import { useState, useEffect, useMemo } from 'react';

import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';

import {
  productFilterVar,
  seenTourListProductsVar,
} from 'apollo/reactiveVariables';
import PageTitle from 'components/layout/PageTitle';
import { showTourGuideListProductsLocalStorage } from 'components/onboarding/helpers';
import TourGuide from 'components/onboarding/TourGuide';
import SkeletonListData from 'components/skeletons/ListData';
import EditableCell from 'components/table/EditableCells';
import Tag from 'components/Tag';
import useSnackbarAlert from 'custom-hooks/useSnackbarAlert';
import { UPDATE_PRODUCT, GET_PRODUCTS } from 'gql/catalogs';
import GET_FILTERS from 'gql/filters';
import { GENERATE_PRODUCTS_REPORT } from 'gql/reports';
import STORAGE_OPTIONS from 'lib/storageOptions';
import HeaderListData from 'views/commons/HeaderListData';
import ListServerData from 'views/commons/ListServerData';

import ProductFilters from './ProductFilters';
// Values to be updated
const FORM_VALUES = ['storageType', 'name', 'measures'];
const INVALID_VALUE_MESSAGE = 'Inválido';
const REQUIRED_VALUE_MESSAGE = 'Requerido';
// TODO: refactor measures
function ListProducts() {
  const [editableRowIndex, setEditableRowIndex] = useState(-1);

  const { SnackbarAlert, displaySnackbarAlert } = useSnackbarAlert();
  const canEditRow = useMemo(() => false, []);
  const {
    register,
    getValues,
    reset: formReset,
    formState,
    handleSubmit,
  } = useForm({ reValidateMode: 'onBlur' });
  const { errors } = formState;

  // Returns variables used in mutation, receives data from the form and data from the row
  const updateVariables = (_formData, original) => {
    const variables = {
      input: {
        id: original.id,
        name: _formData.name,
        storageType: _formData.storageType.value,
      },
    };
    if (_formData.measures.weight.value) {
      variables.input.measures = {
        height: {
          value: parseFloat(_formData.measures.height.value),
          unit: 'cm',
        },
        length: {
          value: parseFloat(_formData.measures.length.value),
          unit: 'cm',
        },
        width: {
          value: parseFloat(_formData.measures.width.value),
          unit: 'cm',
        },
        weight: {
          value: parseFloat(_formData.measures.weight.value),
          unit: 'kg',
        },
      };
    }
    return variables;
  };

  const [updateRow, { loading: updateRowLoading, reset }] = useMutation(
    UPDATE_PRODUCT,
    {
      onError: () => {
        displaySnackbarAlert(
          `Producto no ha sido actualizado: Ha ocurrido un error`,
          'error',
        );
        setEditableRowIndex(-1);
        reset();
      },
      onCompleted: () => {
        displaySnackbarAlert('Producto actualizado correctamente', 'success');
        setEditableRowIndex(-1);
        reset();
      },
    },
  );
  const editFormData = {
    formValues: FORM_VALUES,
    updateVariables,
    formHooks: { handleSubmit, formReset, getValues, errors },
    mutationFunction: updateRow,
    loading: updateRowLoading,
    editableRowIndex,
    setEditableRowIndex,
  };

  const productFilter = useReactiveVar(productFilterVar);
  const seenTourListProducts = useReactiveVar(seenTourListProductsVar);

  const TOUR_STEPS = [
    {
      title: 'Productos',
      target: '#pageTitle',
      content: 'Puedes visualizar todos los productos registrados',
    },
    {
      title: 'Filtros',
      target: '#filterBox',
      content:
        'Puedes filtrar por vendedor y buscar por sku, producto o tipo de almacenaje',
    },
    {
      title: 'Ordenar',
      target: '#tableHead',
      content:
        'Puedes ordenar por Nombre, SKU o Tipo del Almacenaje del Producto',
    },
    {
      title: 'Exportar',
      target: '#exportButton',
      content:
        'Puedes exportar los datos con los filtros seleccionados actuales. Se enviarán por mail en formato excel',
    },
  ];
  const [run, setRun] = useState(!seenTourListProducts);
  const [filterOptions, setFilterOptions] = useState({
    sellers: [],
  });

  const [filters, setFilters] = useState(productFilter);
  useEffect(() => {
    productFilterVar(filters);
  }, [filters]);

  const { loading } = useQuery(GET_FILTERS, {
    onCompleted: ({
      getFilters: {
        catalogFilterOptions: { sellers: _sellers },
      },
    }) => {
      const sellers = _sellers.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
      setFilterOptions({ sellers });
      setFilters((prev) => ({
        ...prev,
        sellerId: productFilter.sellerId || _sellers[0]?.id,
      }));
    },
  });

  const [openExportDataForm, setOpenExportDataForm] = useState(false);
  const handleClickOpenExportDataForm = () => {
    setOpenExportDataForm(true);
  };
  const handleCloseExportDataForm = () => {
    setOpenExportDataForm(false);
  };

  const report = {
    hasReport: true,
    reportProperties: {
      mutation: GENERATE_PRODUCTS_REPORT,
      type: 'products',
      dialogFunctions: {
        open: openExportDataForm,
        setOpen: setOpenExportDataForm,
        handleClickOpen: handleClickOpenExportDataForm,
        handleClose: handleCloseExportDataForm,
      },
      filterOptions,
    },
  };

  const columns = useMemo(
    () => [
      {
        Header: <HeaderListData>Nombre</HeaderListData>,
        accessor: 'name',
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value: initialValue,
          // eslint-disable-next-line react/prop-types
          row: { index },
        }) => {
          const cellRender = (value) => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography>{value}</Typography>
            </Box>
          );
          const cellEditingRender = () => (
            <TextField
              size="small"
              label="Nombre"
              error={Boolean(errors.name)}
              helperText={(errors.name && errors.name.message) || ' '}
              // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
              {...register('name', {
                required: REQUIRED_VALUE_MESSAGE, // JS only: <p>error message</p> TS only support string
              })}
              sx={{ m: 1, width: '25ch' }}
            />
          );

          return (
            <EditableCell
              initialValue={initialValue}
              rowIndex={index}
              editableRowIndex={editableRowIndex}
              cellRender={cellRender}
              cellEditingRender={cellEditingRender}
            />
          );
        },
      },
      {
        Header: <HeaderListData>SKU</HeaderListData>,
        accessor: 'sku',
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value,
          // eslint-disable-next-line react/prop-types
          row: { index },
        }) => {
          const cellRender = (defaultValue) => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography>{defaultValue}</Typography>
            </Box>
          );
          const cellEditingRender = () => (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography>{value}</Typography>
              <Typography> &nbsp;</Typography>
            </Box>
          );
          return (
            <EditableCell
              initialValue={value}
              rowIndex={index}
              editableRowIndex={editableRowIndex}
              cellRender={cellRender}
              cellEditingRender={cellEditingRender}
            />
          );
        },
      },
      {
        Header: <HeaderListData>EAN</HeaderListData>,
        accessor: 'barcode',
        disableSortBy: true,
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value,
        }) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* eslint-disable-next-line react/prop-types */}
            {value.map((ean) => (
              <div key={ean}>
                <Chip label={ean} size="small" />
              </div>
            ))}
          </Box>
        ),
      },
      {
        Header: <HeaderListData>Medidas</HeaderListData>,
        accessor: 'measures',
        disableSortBy: true,
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value: initialValue,
          // eslint-disable-next-line react/prop-types
          row: { index },
        }) => {
          const cellRender = (value) => {
            const { length, width, height } = value || {};
            const missingAllDimensions =
              !value ||
              Object.values({ length, width, height }).filter(
                (dimension) => dimension?.value === '?',
              ).length === 3;
            return (
              <Tooltip
                title={
                  !missingAllDimensions
                    ? `Largo x Ancho x Altura.`
                    : 'No existen dimensiones inscritas'
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>
                    {!missingAllDimensions
                      ? `${value?.length?.value} x ${value?.width?.value} x ${value?.height?.value}`
                      : ''}
                  </Typography>
                  <Typography variant="caption">
                    {!missingAllDimensions
                      ? `${value?.length?.unit} x ${value?.width?.unit} x ${value?.height?.unit}`
                      : '---'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          };
          const cellEditingRender = () => (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <TextField
                label="Largo"
                type="number"
                size="small"
                error={Boolean(errors.measures?.length)}
                helperText={
                  errors.measures?.length &&
                  errors.measures.length.value.message
                }
                // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
                {...register('measures.length.value', {
                  validate: {
                    measureMissing: (v) =>
                      !!v ===
                        Object.values(getValues('measures')).some(
                          (el) => el.value,
                        ) || REQUIRED_VALUE_MESSAGE,
                    positive: (v) =>
                      parseFloat(v, 10) > 0 || INVALID_VALUE_MESSAGE,
                  },
                })}
                sx={{ m: 1, width: '14ch' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' },
                }}
              />
              <TextField
                label="Ancho"
                type="number"
                size="small"
                error={Boolean(errors.measures?.width)}
                helperText={
                  errors.measures?.width && errors.measures.width.value.message
                }
                // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
                {...register('measures.width.value', {
                  validate: {
                    measureMissing: (v) =>
                      !!v ===
                        Object.values(getValues('measures')).some(
                          (el) => el.value,
                        ) || REQUIRED_VALUE_MESSAGE,
                    positive: (v) =>
                      parseFloat(v, 10) > 0 || INVALID_VALUE_MESSAGE,
                  },
                })}
                sx={{ m: 1, width: '14ch' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' },
                }}
              />
              <TextField
                label="Alto"
                type="number"
                size="small"
                error={Boolean(errors.measures?.height)}
                helperText={
                  (errors.measures?.height &&
                    errors.measures.height.value.message) ||
                  ' '
                }
                // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
                {...register('measures.height.value', {
                  validate: {
                    measureMissing: (v) =>
                      !!v ===
                        Object.values(getValues('measures')).some(
                          (el) => el.value,
                        ) || REQUIRED_VALUE_MESSAGE,
                    positive: (v) =>
                      parseFloat(v, 10) > 0 || INVALID_VALUE_MESSAGE,
                  },
                })}
                sx={{ m: 1, width: '14ch' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' },
                }}
              />
            </Box>
          );
          return (
            <EditableCell
              initialValue={initialValue}
              rowIndex={index}
              editableRowIndex={editableRowIndex}
              cellRender={cellRender}
              cellEditingRender={cellEditingRender}
            />
          );
        },
      },
      {
        Header: <HeaderListData>Peso</HeaderListData>,
        id: 'weight',
        accessor: 'measures',
        disableSortBy: true,
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value: initialValue,
          // eslint-disable-next-line react/prop-types
          row: { index },
        }) => {
          const cellRender = (value) => {
            const existsWeight =
              // eslint-disable-next-line react/prop-types
              value?.weight?.value && value?.weight?.value !== '?';
            return (
              <Tooltip title={existsWeight ? '' : 'No existe peso inscrito'}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography>
                    {existsWeight ? `${value.weight.value}` : ''}
                  </Typography>
                  <Typography variant="caption">
                    {existsWeight ? `${value.weight.unit}` : '---'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          };
          const cellEditingRender = () => (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <TextField
                label="Peso"
                type="number"
                size="small"
                error={Boolean(errors.measures?.weight)}
                helperText={
                  (errors.measures?.weight &&
                    errors.measures.weight.value.message) ||
                  ' '
                }
                // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
                {...register('measures.weight.value', {
                  validate: {
                    measureMissing: (v) =>
                      !!v ===
                        Object.values(getValues('measures')).some(
                          (el) => el.value,
                        ) || REQUIRED_VALUE_MESSAGE,
                    positive: (v) =>
                      parseFloat(v, 10) > 0 || INVALID_VALUE_MESSAGE,
                  },
                })}
                sx={{ width: '13ch' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' },
                }}
              />
            </Box>
          );
          return (
            <EditableCell
              initialValue={initialValue}
              rowIndex={index}
              editableRowIndex={editableRowIndex}
              cellRender={cellRender}
              cellEditingRender={cellEditingRender}
            />
          );
        },
      },
      {
        Header: <HeaderListData>Tipo de almacenaje</HeaderListData>,
        accessor: 'storageType',
        Cell: ({
          // eslint-disable-next-line react/prop-types
          value: initialValue,
          // eslint-disable-next-line react/prop-types
          row: { index },
        }) => {
          const cellRender = (value) => (
            <Tooltip title="Los tipos de almacenamiento pueden ser seco, refrigerado y congelado.">
              <span>
                <Tag label={value.label} type={value.value} />
              </span>
            </Tooltip>
          );
          const cellEditingRender = () => (
            <TextField
              label="Tipo de almacenaje"
              select
              size="small"
              helperText=" "
              // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
              {...register('storageType')}
              sx={{ width: '17ch' }}
              defaultValue={STORAGE_OPTIONS.find(
                // eslint-disable-next-line react/prop-types
                (el) => el.value === initialValue.value,
              )}
            >
              {STORAGE_OPTIONS.map((option) => (
                <MenuItem
                  key={`${option.value}`}
                  value={STORAGE_OPTIONS.find(
                    (el) => el.value === option.value,
                  )}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          );
          return (
            <EditableCell
              initialValue={initialValue}
              rowIndex={index}
              editableRowIndex={editableRowIndex}
              cellRender={cellRender}
              cellEditingRender={cellEditingRender}
            />
          );
        },
      },
    ],
    [formState],
  );
  const handleGlobalFilter = (globalFilter) => {
    setFilters((prev) => ({
      ...prev,
      globalFilter,
    }));
  };
  if (loading)
    return (
      <>
        <PageTitle>Productos</PageTitle>
        <SkeletonListData />
      </>
    );
  return (
    <>
      <PageTitle
        handleClick={() => {
          setRun(true);
        }}
      >
        Productos
      </PageTitle>
      <TourGuide
        steps={TOUR_STEPS}
        run={run}
        setRun={setRun}
        finishTour={showTourGuideListProductsLocalStorage}
      />
      <ListServerData
        report={report}
        query={GET_PRODUCTS}
        filters={{
          components: (
            <ProductFilters
              filters={filters}
              setFilters={setFilters}
              sellersOptions={filterOptions.sellers}
            />
          ),
          helperTextSearchBox: 'Buscar por producto, sku o tipo de almacenaje',
        }}
        queryVariables={{
          filters: {
            hasComponents: false,
            ...filters,
          },
        }}
        dataKey="getProducts"
        handleGlobalFilter={handleGlobalFilter}
        editFormProperties={canEditRow && editFormData}
        initGlobalFilter={productFilter.globalFilter}
        columns={columns}
        filterOptions={filterOptions}
      />
      <SnackbarAlert />
    </>
  );
}

export default ListProducts;
